import { useState, useCallback } from 'react'

import { uniqueId } from 'lodash-es'
import { load } from 'scrivito'

import {
  expandEnumValuesWithSameTitle,
  isMultiValueOperator,
  normalizeOperatorForEnumValues,
  OPERATOR_DEFINITIONS,
  OperatorType,
} from './config'
import { FilterStructure, FilterCondition, SavedFilterStructure } from './types'
import {
  SchemaAttribute,
  SchemaAttributes,
  SchemaReferenceFilter,
  SchemaRelation,
} from '@/Data/types'
import { ReferenceFilter } from '@/Data/typesV2'
import { getReferenceDisplayValue } from '@/utils/CRUD/read/reference'
import { getSchemaName } from '@/utils/dataClassMap'
import { getSchema } from '@/utils/getSchema'

const normalizeValueForOperator = (operator: string, value: unknown) => {
  if (isMultiValueOperator(operator)) {
    if (Array.isArray(value)) return value
    if (value === null || value === undefined || value === '') return []
    return [value]
  }

  if ((operator === 'eq' || operator === 'neq') && Array.isArray(value)) {
    return value[0] ?? null
  }

  return value
}

const resolveReferenceLabel = async (
  value: unknown,
  referenceTo?: string,
  referenceFilter?: SchemaReferenceFilter,
) => {
  if (!referenceTo) return undefined

  const filter = referenceFilter as ReferenceFilter | undefined

  if (Array.isArray(value) && value.length > 0) {
    const valueArray = value as string[]
    return load(() => {
      const displayNames = valueArray
        .map((id: string) => getReferenceDisplayValue(id, referenceTo, filter))
        .filter((name: string) => name)
      return displayNames.length > 0 ? displayNames.join(', ') : undefined
    })
  }

  if (typeof value === 'string' && value) {
    return load(() => {
      return getReferenceDisplayValue(value, referenceTo, filter) || undefined
    })
  }

  return undefined
}

interface UseFilterBuilderOptions {
  entityName: string
  initialFilter?: SavedFilterStructure
  baseSchemaAttributes?: SchemaAttributes
  schemaRelations?: SchemaRelation[]
}

interface UseFilterBuilderReturn {
  filter: FilterStructure
  setFilter: React.Dispatch<React.SetStateAction<FilterStructure>>
  resetFilter: () => void
  loadFromSavedFilter: (savedFilter: SavedFilterStructure) => Promise<void>
  validateFilter: () => {
    isValid: boolean
    fieldErrors: Set<string>
    valueErrors: Set<string>
  }
  errors: {
    field: Set<string>
    value: Set<string>
  }
  clearError: (type: 'field' | 'value', id: string) => void
  toSavedFormat: () => SavedFilterStructure
  createEmptyFilter: () => FilterStructure
}

/**
 * Hook to manage filter state, validation, and persistence
 */
export function useFilterBuilder({
  entityName,
  initialFilter,
  baseSchemaAttributes,
  schemaRelations,
}: UseFilterBuilderOptions): UseFilterBuilderReturn {
  const [errors, setErrors] = useState<{
    field: Set<string>
    value: Set<string>
  }>({
    field: new Set(),
    value: new Set(),
  })

  const clearError = useCallback((type: 'field' | 'value', id: string) => {
    setErrors((prev) => {
      const next = new Set(prev[type])
      next.delete(id)
      return {
        ...prev,
        [type]: next,
      }
    })
  }, [])

  const createEmptyFilter = useCallback(
    (): FilterStructure => ({
      groups: [
        {
          id: uniqueId('group_'),
          conditions: [
            {
              id: uniqueId('cond_'),
              entity: entityName,
              field: '',
              operator: 'eq',
              value: '',
            },
          ],
        },
      ],
    }),
    [entityName],
  )

  const [filter, setFilter] = useState<FilterStructure>(() => {
    if (initialFilter?.or?.length) {
      // Convert SavedFilterStructure to FilterStructure by adding id fields
      return {
        groups: initialFilter.or.map((group) => ({
          id: uniqueId('group_'),
          conditions: (group.and ?? []).map((condition) => ({
            id: uniqueId('cond_'),
            field: condition.field,
            entity: entityName,
            operator: condition.op,
            value: normalizeValueForOperator(condition.op, condition.value),
          })),
        })),
      }
    }
    return createEmptyFilter()
  })

  const resetFilter = useCallback(() => {
    setFilter(createEmptyFilter())
  }, [createEmptyFilter])

  // Convert a SavedFilterStructure to the internal FilterStructure (async, resolves reference labels)
  const loadFromSavedFilter = useCallback(
    async (parsedFilter: SavedFilterStructure) => {
      if (!baseSchemaAttributes) return
      if (!parsedFilter?.or?.length) return

      try {
        const internalFilter: FilterStructure = {
          groups: await Promise.all(
            parsedFilter.or.map(async (group) => ({
              id: uniqueId('group_'),
              conditions: await Promise.all(
                (group.and ?? []).map(async (condition) => {
                  // Parse field path to extract referenceField and field
                  let entity = entityName
                  let referenceField: string | undefined = undefined
                  let field = condition.field
                  let valueAttribute: SchemaAttribute | undefined = undefined
                  let valueReferenceTo: string | undefined = undefined
                  let valueReferenceFilter: SchemaReferenceFilter | undefined =
                    undefined

                  // Parse field path examples:
                  // - "staff.id" (m:n relation)
                  // - "accountManager" (m:1 reference field)
                  // - "parent.name" (nested reference)
                  // - "name" (regular field)
                  const fieldParts = condition.field.split('.')

                  if (fieldParts.length > 1) {
                    // Has reference path
                    referenceField = fieldParts[0]
                    field = fieldParts.slice(1).join('.')

                    // First check if this matches a schema relation (many-to-many or configured relations)
                    const matchingRelation = schemaRelations?.find(
                      (rel) => rel.relationPath === referenceField,
                    )

                    if (matchingRelation) {
                      // Use the relation's entity name
                      entity = matchingRelation.entityName
                      // For m:n relations saved as "staff.id", we've now split it into:
                      // referenceField="staff", field="id"
                      // Convert field from "id" to "_id" for the internal format
                      // (internal format uses "_id" for record selection)
                      if (field === 'id') {
                        field = '_id'
                      }
                    } else {
                      // Fall back to checking baseSchemaAttributes for regular reference fields
                      const refAttribute = referenceField
                        ? baseSchemaAttributes[referenceField]
                        : undefined
                      if (refAttribute?.referenceTo) {
                        entity = refAttribute.referenceTo.toLowerCase()
                      }
                    }

                    // Check if the final field is also a reference (for nested references)
                    if (entity !== entityName && field !== '_id') {
                      const { finalFieldAttribute } = await load(() => {
                        const schemaName = getSchemaName(entity)
                        const { schema } = getSchema(schemaName)
                        const schemaAttributes = schema?.get(
                          'attributes',
                        ) as SchemaAttributes
                        const finalFieldAttribute = schemaAttributes?.[field]
                        return { finalFieldAttribute }
                      })

                      valueAttribute = finalFieldAttribute

                      if (finalFieldAttribute?.type === 'reference') {
                        valueReferenceTo = finalFieldAttribute.referenceTo
                        valueReferenceFilter =
                          finalFieldAttribute.referenceFilter
                      }
                    }
                  } else {
                    // Single field - only m:1 reference fields (m:n must use "relation.id" format)
                    const fieldAttribute =
                      baseSchemaAttributes?.[condition.field]
                    valueAttribute = fieldAttribute
                    if (fieldAttribute?.type === 'reference') {
                      // m:1 relation (e.g., "accountManager")
                      // Convert to _id format so the row uses record selection.
                      valueReferenceTo = fieldAttribute.referenceTo
                      valueReferenceFilter = fieldAttribute.referenceFilter
                      referenceField = condition.field
                      field = '_id'
                      if (fieldAttribute.referenceTo) {
                        entity = fieldAttribute.referenceTo.toLowerCase()
                      }
                    }
                  }

                  const operator = normalizeOperatorForEnumValues(
                    condition.op,
                    valueAttribute?.enumValues,
                  )
                  const normalizedValue = isMultiValueOperator(operator)
                    ? expandEnumValuesWithSameTitle(
                        normalizeValueForOperator(operator, condition.value),
                        valueAttribute?.enumValues,
                      )
                    : normalizeValueForOperator(operator, condition.value)

                  // Fetch display names for reference/record fields
                  const label = await resolveReferenceLabel(
                    normalizedValue,
                    valueReferenceTo ||
                      (field === '_id'
                        ? entity.charAt(0).toUpperCase() + entity.slice(1)
                        : undefined),
                    valueReferenceFilter,
                  )

                  return {
                    id: uniqueId('cond_'),
                    entity,
                    referenceField,
                    field,
                    operator,
                    value: normalizedValue,
                    label,
                  } as FilterCondition
                }),
              ),
            })),
          ),
        }

        setFilter(internalFilter)
      } catch (e) {
        console.error('Failed to load filter from saved data:', e)
      }
    },
    [baseSchemaAttributes, schemaRelations, entityName],
  )

  // Validate filter and return validation result
  const validateFilter = useCallback(() => {
    const fieldErrors = new Set<string>()
    const valueErrors = new Set<string>()
    const filterToValidate = filter.groups.some(
      (group) => group.conditions.length > 0,
    )
      ? filter
      : createEmptyFilter()

    if (filterToValidate !== filter) {
      setFilter(filterToValidate)
    }

    filterToValidate.groups.forEach((group) => {
      group.conditions.forEach((condition) => {
        // Check if field is empty
        if (!condition.field && condition.id) {
          fieldErrors.add(condition.id)
        }

        // Check if value is empty (only when field is set and operator requires value)
        if (condition.field && condition.id) {
          const operatorDef =
            OPERATOR_DEFINITIONS[condition.operator as OperatorType]
          if (operatorDef?.requiresValue) {
            if (condition.value === null || condition.value === undefined) {
              valueErrors.add(condition.id)
            } else if (
              typeof condition.value === 'string' &&
              condition.value.trim() === ''
            ) {
              valueErrors.add(condition.id)
            } else if (
              Array.isArray(condition.value) &&
              condition.value.length === 0
            ) {
              valueErrors.add(condition.id)
            } else if (condition.operator === 'between') {
              let fromValue: string | undefined
              let toValue: string | undefined

              if (Array.isArray(condition.value)) {
                fromValue = condition.value[0]
                toValue = condition.value[1]
              }

              if (!fromValue?.trim() || !toValue?.trim()) {
                valueErrors.add(condition.id)
              } else {
                // Check if start <= end
                const fromDate = new Date(fromValue)
                const toDate = new Date(toValue)
                if (
                  !isNaN(fromDate.getTime()) &&
                  !isNaN(toDate.getTime()) &&
                  fromDate > toDate
                ) {
                  valueErrors.add(condition.id)
                }
              }
            } else if (
              condition.operator === 'inLast' ||
              condition.operator === 'inNext' ||
              condition.operator === 'notInLast' ||
              condition.operator === 'notInNext'
            ) {
              // Validate time-range value {amount, unit}
              if (
                !condition.value ||
                typeof condition.value !== 'object' ||
                !('amount' in condition.value) ||
                !('unit' in condition.value) ||
                !(condition.value as { amount: number }).amount ||
                (condition.value as { amount: number }).amount <= 0
              ) {
                valueErrors.add(condition.id)
              }
            }
          }
        }
      })
    })

    setErrors({ field: fieldErrors, value: valueErrors })

    return {
      isValid: fieldErrors.size === 0 && valueErrors.size === 0,
      fieldErrors,
      valueErrors,
    }
  }, [filter, createEmptyFilter])

  // Convert internal filter to saved format
  const toSavedFormat = useCallback((): SavedFilterStructure => {
    return {
      or: filter.groups.map((group) => ({
        and: group.conditions.map((condition) => {
          // Build field path
          let fieldPath = condition.field
          if (
            condition.entity &&
            condition.entity !== entityName &&
            condition.referenceField
          ) {
            if (condition.field === '_id') {
              // Check if this is a configured collection relation
              const matchingRelation = schemaRelations?.find(
                (rel) => rel.relationPath === condition.referenceField,
              )
              if (matchingRelation) {
                // For collection relations, save as "relationPath.id"
                fieldPath = `${condition.referenceField}.id`
              } else {
                // For m:1 relations, save as just "referenceField"
                fieldPath = condition.referenceField
              }
            } else {
              fieldPath = `${condition.referenceField}.${condition.field}`
            }
          }

          return {
            field: fieldPath,
            op: condition.operator,
            value: normalizeValueForOperator(
              condition.operator,
              condition.value,
            ),
          }
        }),
      })),
    }
  }, [filter, entityName, schemaRelations])

  return {
    filter,
    setFilter,
    resetFilter,
    loadFromSavedFilter,
    validateFilter,
    toSavedFormat,
    createEmptyFilter,
    errors,
    clearError,
  }
}
